import React, { forwardRef, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import MaterialTable from 'material-table';
import { Link } from 'react-router-dom';

import { AuthorizeContext } from '../../RootContext';
import { api_url } from '../../authorize';
import logo from './../../img/lento_logo_transparent.png';

import {
  createMuiTheme,
  makeStyles,
  ThemeProvider,
} from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';

import AddBox from '@material-ui/icons/AddBox';
import ArrowUpward from '@material-ui/icons/ArrowUpward';
import Check from '@material-ui/icons/Check';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import Clear from '@material-ui/icons/Clear';
import DeleteOutline from '@material-ui/icons/DeleteOutline';
import Edit from '@material-ui/icons/Edit';
import FilterList from '@material-ui/icons/FilterList';
import FirstPage from '@material-ui/icons/FirstPage';
import LastPage from '@material-ui/icons/LastPage';
import Remove from '@material-ui/icons/Remove';
import RotateLeftIcon from '@material-ui/icons/RotateLeft';
import SaveAlt from '@material-ui/icons/SaveAlt';
import Search from '@material-ui/icons/Search';
import ViewColumn from '@material-ui/icons/ViewColumn';

const tableIcons = {
  Add: forwardRef((props, ref) => <AddBox {...props} ref={ref} />),
  Check: forwardRef((props, ref) => <Check {...props} ref={ref} />),
  Clear: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Delete: forwardRef((props, ref) => <DeleteOutline {...props} ref={ref} />),
  DetailPanel: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  Edit: forwardRef((props, ref) => <Edit {...props} ref={ref} />),
  Export: forwardRef((props, ref) => <SaveAlt {...props} ref={ref} />),
  Filter: forwardRef((props, ref) => <FilterList {...props} ref={ref} />),
  FirstPage: forwardRef((props, ref) => <FirstPage {...props} ref={ref} />),
  LastPage: forwardRef((props, ref) => <LastPage {...props} ref={ref} />),
  NextPage: forwardRef((props, ref) => <ChevronRight {...props} ref={ref} />),
  PreviousPage: forwardRef((props, ref) => <ChevronLeft {...props} ref={ref} />),
  Reset: forwardRef((props, ref) => <RotateLeftIcon {...props} ref={ref} />),
  ResetSearch: forwardRef((props, ref) => <Clear {...props} ref={ref} />),
  Search: forwardRef((props, ref) => <Search {...props} ref={ref} />),
  SortArrow: forwardRef((props, ref) => <ArrowUpward {...props} ref={ref} />),
  ThirdStateCheck: forwardRef((props, ref) => <Remove {...props} ref={ref} />),
  ViewColumn: forwardRef((props, ref) => <ViewColumn {...props} ref={ref} />),
};

const theme = createMuiTheme({
  palette: {
    primary: {
      main: '#0b819d',
    },
    secondary: {
      main: '#18a39a',
    },
    background: {
      default: '#f4f7fb',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    h6: {
      fontWeight: 700,
    },
  },
  overrides: {
    MuiButton: {
      root: {
        textTransform: 'none',
        fontWeight: 700,
        borderRadius: 9,
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: 9,
      },
    },
    MuiPaper: {
      rounded: {
        borderRadius: 14,
      },
    },
  },
});

const useStyles = makeStyles((muiTheme) => ({
  page: {
    minHeight: 'calc(100vh - 72px)',
    padding: '28px 20px 44px',
    boxSizing: 'border-box',
    background: '#f3f7f9',
    color: '#173247',
    fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif',

    [muiTheme.breakpoints.down('sm')]: {
      padding: '20px 16px 32px',
    },
  },
  shell: {
    width: '100%',
    maxWidth: 'none',
    margin: 0,
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 20,
    marginBottom: 20,
    padding: 0,
    border: 'none',
    borderRadius: 0,
    background: 'transparent',
    boxShadow: 'none',

    [muiTheme.breakpoints.down('sm')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
      gap: 12,
    },
  },
  eyebrow: {
    margin: 0,
    color: '#0b819d',
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: '0.10em',
    textTransform: 'uppercase',
  },
  title: {
    margin: '5px 0 0',
    color: '#173247',
    fontSize: 32,
    lineHeight: 1.15,
    fontWeight: 800,
    letterSpacing: '-0.025em',

    [muiTheme.breakpoints.down('xs')]: {
      fontSize: 27,
    },
  },
  subtitle: {
    maxWidth: 760,
    margin: '8px 0 0',
    color: '#607888',
    fontSize: 16,
    lineHeight: 1.55,
    fontWeight: 500,
  },
  headerMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  metaPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 7,
    minHeight: 0,
    padding: 0,
    borderRadius: 0,
    background: 'transparent',
    border: 'none',
    boxShadow: 'none',
    color: '#607888',
    fontSize: 14,
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  metaDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#0b819d',
    boxShadow: 'none',
  },
  card: {
    width: '100%',
    overflow: 'hidden',
    borderRadius: 16,
    border: '1px solid #dce7ec',
    background: '#ffffff',
    boxShadow: '0 16px 36px rgba(13, 46, 67, 0.08)',

    '& > .MuiPaper-root, & .MuiPaper-root': {
      boxShadow: 'none !important',
      borderRadius: '0 !important',
    },
    '& .MuiToolbar-root': {
      minHeight: '68px !important',
      padding: '10px 18px !important',
      borderBottom: '1px solid #e2ebef',
      background: '#ffffff',
    },
    '& .MuiTableContainer-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      overflowX: 'hidden !important',
    },
    '& .MuiTable-root': {
      width: '100% !important',
      maxWidth: '100% !important',
      minWidth: '0 !important',
      tableLayout: 'fixed !important',
      fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif !important',
    },
    '& .MuiTableCell-root': {
      boxSizing: 'border-box !important',
      minWidth: '0 !important',
      paddingLeft: '7px !important',
      paddingRight: '7px !important',
      overflow: 'hidden !important',
      fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif !important',
    },
    '& .MuiTableCell-head': {
      paddingTop: '12px !important',
      paddingBottom: '12px !important',
      background: '#f1f6f8 !important',
      color: '#173247 !important',
      borderBottom: '1px solid #dce7ec !important',
      fontSize: '13px !important',
      fontWeight: '700 !important',
      lineHeight: '1.2 !important',
      whiteSpace: 'normal !important',
      wordBreak: 'normal !important',
    },
    '& .MuiTableCell-body': {
      paddingTop: '11px !important',
      paddingBottom: '11px !important',
      borderBottom: '1px solid #edf1f3 !important',
      color: '#244154 !important',
      fontSize: '14px !important',
      fontWeight: '600 !important',
      lineHeight: '1.3 !important',
      verticalAlign: 'middle',
      whiteSpace: 'normal !important',
      overflowWrap: 'anywhere !important',
    },
    '& .MuiTableBody-root .MuiTableRow-root:hover': {
      backgroundColor: '#f5fafb !important',
    },
    '& .MuiInputBase-root': {
      color: '#244154',
      fontFamily: 'Segoe UI, Tahoma, Arial, sans-serif',
      fontSize: 14,
    },
    '& .MuiTablePagination-root': {
      borderTop: '1px solid #e2ebef',
      background: '#ffffff',
      color: '#607888',
    },
    '& .MuiIconButton-root': {
      color: '#49647d',
    },
    '& .MuiIconButton-root:hover': {
      background: '#eef7f9',
      color: '#0b819d',
    },

    // Center the action icons in the right-most Actions column.
    '& .MuiTableCell-root:last-child': {
      textAlign: 'center !important',
    },
    '& .MuiTableCell-root:last-child > div': {
      width: '100% !important',
      display: 'flex !important',
      alignItems: 'center !important',
      justifyContent: 'center !important',
    },
    '& .MuiTableCell-root:last-child .MuiIconButton-root': {
      margin: '0 2px !important',
    },
  },
  cardIntro: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 14,
    padding: '14px 18px',
    borderBottom: '1px solid #e2ebef',
    background: '#ffffff',
  },
  cardLabel: {
    margin: 0,
    color: '#173247',
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: '0.01em',
  },
  cardHint: {
    margin: 0,
    color: '#718897',
    fontSize: 13,
    fontWeight: 500,
  },
  accessBadge: {
    flex: '0 0 auto',
    padding: '7px 11px',
    borderRadius: 999,
    color: '#176B5B',
    background: 'linear-gradient(135deg, #EAF8F2 0%, #E8F7F6 100%)',
    border: '1px solid #B9DDD2',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.055em',
    textTransform: 'uppercase',
  },
  nameLink: {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    color: '#176B5B',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    fontSize: 'inherit',
    fontWeight: 700,
    textDecoration: 'underline',
    textDecorationColor: 'rgba(23, 107, 91, 0.42)',
    textDecorationThickness: '1px',
    textUnderlineOffset: '2px',
    cursor: 'pointer',

    '&:hover': {
      color: '#0F5B4D',
      textDecorationColor: '#0F5B4D',
    },
  },
  officeCell: {
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
  addressCell: {
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    overflowWrap: 'anywhere',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    lineHeight: 'inherit',
  },
  emailCell: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'visible',
    color: 'inherit',
    fontSize: '13px',
    fontWeight: 'inherit',
    lineHeight: 1.3,
    letterSpacing: 0,
    textOverflow: 'clip',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 5,
    width: '100%',
    minWidth: 0,
    maxWidth: 86,
    padding: '5px 6px',
    boxSizing: 'border-box',
    borderRadius: 999,
    fontSize: 9.5,
    fontWeight: 800,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  },
  statusActive: {
    color: '#0D6B4B',
    background: 'linear-gradient(135deg, #DCF8EB 0%, #CFF2E3 100%)',
    border: '1px solid #A9DEC8',
    boxShadow: '0 4px 10px rgba(13, 107, 75, 0.10)',
  },
  statusInactive: {
    color: '#5C6470',
    background: 'linear-gradient(135deg, #F2F4F7 0%, #E7EBF0 100%)',
    border: '1px solid #D2D9E1',
  },
  statusDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: 'currentColor',
  },
  loadingScreen: {
    position: 'fixed',
    top: 72,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: 1200,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    background: '#ffffff',
  },
  loadingLogoWrap: {
    width: 'min(680px, 62vw)',
    maxWidth: 'calc(100vw - 48px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    [muiTheme.breakpoints.down('sm')]: {
      width: 'min(520px, 78vw)',
    },

    [muiTheme.breakpoints.down('xs')]: {
      width: 'min(390px, 86vw)',
    },
  },
  loadingLogo: {
    display: 'block',
    width: '100%',
    height: 'auto',
    objectFit: 'contain',
    background: 'transparent',
    filter: 'none',
  },
  errorCard: {
    maxWidth: 600,
    margin: '70px auto',
    padding: 28,
    borderRadius: 18,
    background: '#ffffff',
    border: '1px solid #efc9c4',
    boxShadow: '0 18px 48px rgba(26, 53, 80, 0.09)',
    textAlign: 'center',
  },
  errorTitle: {
    margin: 0,
    color: '#9b3e36',
    fontSize: 20,
    fontWeight: 800,
  },
  errorText: {
    margin: '9px 0 0',
    color: '#785a58',
    fontSize: 14,
    lineHeight: 1.55,
  },
  dialogPaper: {
    borderRadius: 16,
  },
  dialogTitle: {
    color: '#243b52',
    fontWeight: 800,
  },
  dangerButton: {
    color: '#ffffff',
    background: '#b9473d',

    '&:hover': {
      background: '#9e382f',
    },
  },
}));

const MRIList = () => {
  const classes = useStyles();
  const {
    token,
    authBody,
    setAuthenticated,
    setAuthBody,
  } = useContext(AuthorizeContext);

  const profile = authBody || { role: 'visitor' };
  const hasRole = (roles) => roles.includes(profile.role);

  const [state, setState] = useState({
    data: [],
    loading: true,
    error: '',
    dialogOpen: false,
    rowData: null,
    resetting: false,
  });

  const isAdminDirectory = hasRole(['super_admin', 'write_admin', 'read_admin', 'partner_admin']);
  const canEdit = hasRole(['super_admin']);

  const closeDialog = () => {
    if (state.resetting) {
      return;
    }

    setState((previous) => ({
      ...previous,
      dialogOpen: false,
      rowData: null,
    }));
  };

  const handleSessionExpired = () => {
    setAuthenticated(false);
    setAuthBody({ role: 'visitor' });
    alert('You have been logged out due to inactivity. Please log in again.');
  };

  useEffect(() => {
    let isMounted = true;

    const fetchDirectory = async () => {
      try {
        const response = await axios({
          url: `https://${api_url}/api/mri_center_list`,
          method: 'get',
          headers: {
            'x-access-token': token,
          },
        });

        if (!isMounted) {
          return;
        }

        if (response.data && response.data.message) {
          setState((previous) => ({
            ...previous,
            loading: false,
            error: response.data.message,
          }));
          return;
        }

        if (response.data && response.data.validation) {
          setState((previous) => ({
            ...previous,
            loading: false,
            error: response.data.validation.replace('_', ' '),
          }));
          return;
        }

        if (response.data && response.data.notLoggedIn) {
          handleSessionExpired();
          return;
        }

        setState((previous) => ({
          ...previous,
          loading: false,
          data: Array.isArray(response.data) ? response.data : [],
        }));
      } catch (requestError) {
        if (!isMounted) {
          return;
        }

        setState((previous) => ({
          ...previous,
          loading: false,
          error:
            (requestError.response
              && requestError.response.data
              && requestError.response.data.message)
            || requestError.message
            || 'Unable to load this directory.',
        }));
      }
    };

    fetchDirectory();

    return () => {
      isMounted = false;
    };
  }, [token, setAuthBody, setAuthenticated]);

  const availabilityColumn = useMemo(
    () => ({
      title: 'Availability',
      field: 'available',
      width: 90,
      type: 'boolean',
      filtering: true,
      sorting: true,
      render: (rowData) => {
        const available = Boolean(rowData.available);

        return (
          <span
            className={`${classes.statusBadge} ${
              available ? classes.statusActive : classes.statusInactive
            }`}
          >
            <span className={classes.statusDot} />
            {available ? 'Available' : 'Unavailable'}
          </span>
        );
      },
    }),
    [
      classes.statusActive,
      classes.statusBadge,
      classes.statusDot,
      classes.statusInactive,
    ]
  );

  const adminColumns = useMemo(
    () => [
      {
        title: 'Center Name',
        field: 'Office_Name',
        width: 112,
        render: (rowData) => (
          <div className={classes.officeCell}>
            {rowData.Office_Name || '—'}
          </div>
        ),
      },
      {
        title: 'First Name',
        field: 'First_Name',
        width: 65,
      },
      {
        title: 'Middle Name',
        field: 'Middle_Name',
        width: 50,
      },
      {
        title: 'Last Name',
        field: 'Last_Name',
        width: 82,
      },
      {
        title: 'Country',
        field: 'Country',
        width: 48,
      },
      {
        title: 'City',
        field: 'City',
        width: 70,
      },
      {
        title: 'Postal Code',
        field: 'Postal_Code',
        width: 62,
      },
      {
        title: 'Street',
        field: 'Street',
        width: 118,
        render: (rowData) => (
          <div className={classes.addressCell}>{rowData.Street || '—'}</div>
        ),
      },
      {
        title: 'State/Province',
        field: 'State_Province',
        width: 58,
      },
      {
        title: 'Email Address',
        field: 'Email_Address',
        width: 250,
        cellStyle: {
          minWidth: 0,
          whiteSpace: 'normal',
          overflow: 'visible',
          textOverflow: 'clip',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        },
        headerStyle: {
          minWidth: 0,
          whiteSpace: 'normal',
        },
        render: (rowData) => (
          <span
            className={classes.emailCell}
            title={rowData.Email_Address || ''}
          >
            {rowData.Email_Address || '—'}
          </span>
        ),
      },
      {
        title: 'Phone',
        field: 'Phone',
        width: 90,
      },
      {
        title: 'Fax',
        field: 'Fax',
        width: 68,
      },

      availabilityColumn,
    ],
    [availabilityColumn, classes]
  );

  const standardColumns = useMemo(
    () => [
      {
        title: 'Center Name',
        field: 'Office_Name',
        width: 112,
        editable: 'never',
        render: (rowData) => (
          <Link
            className={classes.nameLink}
            to={`/mricentercases/${rowData.Email_Address}`}
          >
            {rowData.Office_Name || '—'}
          </Link>
        ),
      },
      {
        title: 'First Name',
        field: 'First_Name',
        width: 65,
        editable: 'never',
      },
      {
        title: 'Middle Name',
        field: 'Middle_Name',
        width: 50,
        editable: 'never',
      },
      {
        title: 'Last Name',
        field: 'Last_Name',
        width: 82,
        editable: 'never',
      },
      {
        title: 'Office Address',
        field: 'Office_Address',
        width: 175,
        editable: 'never',
        render: (rowData) => (
          <div className={classes.addressCell}>
            {rowData.Street || '—'}
            <br />
            {[rowData.City, rowData.State_Province, rowData.Country]
              .filter(Boolean)
              .join(', ')}
          </div>
        ),
      },
      {
        title: 'Email Address',
        field: 'Email_Address',
        width: 250,
        editable: 'never',
        cellStyle: {
          minWidth: 0,
          whiteSpace: 'normal',
          overflow: 'visible',
          textOverflow: 'clip',
          overflowWrap: 'anywhere',
          wordBreak: 'break-word',
        },
        headerStyle: {
          minWidth: 0,
          whiteSpace: 'normal',
        },
        render: (rowData) => (
          <span
            className={classes.emailCell}
            title={rowData.Email_Address || ''}
          >
            {rowData.Email_Address || '—'}
          </span>
        ),
      },
      {
        title: 'Phone',
        field: 'Phone',
        width: 90,
        editable: 'never',
      },
      {
        title: 'Fax',
        field: 'Fax',
        width: 68,
        editable: 'never',
      },

      availabilityColumn,
    ],
    [availabilityColumn, classes]
  );

  const resetPassword = async () => {
    if (!state.rowData) {
      return;
    }

    setState((previous) => ({ ...previous, resetting: true }));

    try {
      const response = await axios({
        url: `https://${api_url}/api/reset_password?login=${encodeURIComponent(
          state.rowData.Email_Address
        )}`,
        method: 'post',
        headers: {
          'x-access-token': token,
        },
      });

      if (response.data && response.data.message) {
        alert(response.data.message);
        setState((previous) => ({ ...previous, resetting: false }));
        return;
      }

      if (response.data && response.data.validation) {
        alert(response.data.validation.replace('_', ' '));
        setState((previous) => ({ ...previous, resetting: false }));
        return;
      }

      if (response.data && response.data.notLoggedIn) {
        handleSessionExpired();
        return;
      }

      setState((previous) => ({
        ...previous,
        resetting: false,
        dialogOpen: false,
        rowData: null,
      }));
    } catch (requestError) {
      alert(
        (requestError.response
          && requestError.response.data
          && requestError.response.data.message)
        || requestError.message
        || 'Unable to reset the password.'
      );
      setState((previous) => ({ ...previous, resetting: false }));
    }
  };

  const updateRow = (newData, oldData) =>
    new Promise((resolve, reject) => {
      axios({
        url: `https://${api_url}/api/mri_center?email=${encodeURIComponent(
          oldData.Email_Address
        )}`,
        method: 'put',
        data: newData,
        headers: {
          'x-access-token': token,
        },
      })
        .then((response) => {
          if (response.data && response.data.message) {
            alert(response.data.message);
            reject();
            return;
          }

          if (response.data && response.data.validation) {
            alert(response.data.validation.replace('_', ' '));
            reject();
            return;
          }

          if (response.data && response.data.notLoggedIn) {
            handleSessionExpired();
            reject();
            return;
          }

          setState((previous) => {
            const updatedData = [...previous.data];
            const rowIndex = updatedData.indexOf(oldData);

            if (rowIndex >= 0) {
              updatedData[rowIndex] = newData;
            }

            return {
              ...previous,
              data: updatedData,
            };
          });

          resolve();
        })
        .catch((requestError) => {
          alert(
            (requestError.response
              && requestError.response.data
              && requestError.response.data.message)
            || requestError.message
            || 'Unable to save this record.'
          );
          reject();
        });
    });

  if (state.loading) {
    return (
      <div className={classes.loadingScreen}>
        <div className={classes.loadingLogoWrap}>
          <img
            className={classes.loadingLogo}
            src={logo}
            alt="Lento Medical Innovation"
          />
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <main className={classes.page}>
        <div className={classes.errorCard}>
          <h2 className={classes.errorTitle}>Directory unavailable</h2>
          <p className={classes.errorText}>{state.error}</p>
        </div>
      </main>
    );
  }

  const pageTitle = 'MRI Center Directory';
  const pageSubtitle = '';

  return (
    <ThemeProvider theme={theme}>
      <main className={classes.page}>
        <div className={classes.shell}>
          <header className={classes.pageHeader}>
            <div>
              <p className={classes.eyebrow}>Organization directory</p>
              <h1 className={classes.title}>{pageTitle}</h1>
              <p className={classes.subtitle}>{pageSubtitle}</p>
            </div>

            <div className={classes.headerMeta}>
              <span className={classes.metaPill}>
                <span className={classes.metaDot} />
                {state.data.length} {state.data.length === 1 ? 'record' : 'records'}
              </span>
            </div>
          </header>

          <section className={classes.card}>
            <div className={classes.cardIntro}>
              <div>
                <p className={classes.cardLabel}>Directory records</p>
                <p className={classes.cardHint}>
                  Search, sort, filter, and review account details from one place.
                </p>
              </div>


            </div>

            <MaterialTable
              title=""
              columns={isAdminDirectory ? adminColumns : standardColumns}
              data={state.data}
              icons={tableIcons}
              actions={
                canEdit
                  ? [
                      {
                        icon: tableIcons.Reset,
                        tooltip: 'Reset password',
                        onClick: (event, rowData) => {
                          setState((previous) => ({
                            ...previous,
                            dialogOpen: true,
                            rowData,
                          }));
                        },
                      },
                    ]
                  : []
              }
              editable={
                canEdit
                  ? {
                      onRowUpdate: updateRow,
                    }
                  : undefined
              }
              localization={{
                pagination: {
                  labelDisplayedRows: '{from}-{to} of {count}',
                  labelRowsSelect: 'rows',
                  firstAriaLabel: 'First page',
                  firstTooltip: 'First page',
                  previousAriaLabel: 'Previous page',
                  previousTooltip: 'Previous page',
                  nextAriaLabel: 'Next page',
                  nextTooltip: 'Next page',
                  lastAriaLabel: 'Last page',
                  lastTooltip: 'Last page',
                },
                toolbar: {
                  nRowsSelected: '{0} row(s) selected',
                  searchPlaceholder: 'Search directory...',
                  searchTooltip: 'Search directory',
                },
                header: {
                  actions: 'Actions',
                },
                body: {
                  emptyDataSourceMessage: 'No records found',
                  editRow: {
                    deleteText: 'Are you sure you want to delete this record?',
                    cancelTooltip: 'Cancel',
                    saveTooltip: 'Save changes',
                  },
                  filterRow: {
                    filterTooltip: 'Filter',
                  },
                },
              }}
              options={{
                actionsColumnIndex: -1,
                actionsCellStyle: {
                  paddingLeft: 2,
                  paddingRight: 2,
                  textAlign: 'center',
                  verticalAlign: 'middle',
                },
                columnsButton: true,
                debounceInterval: 300,
                draggable: false,
                emptyRowsWhenPaging: false,
                filtering: true,
                pageSize: 15,
                pageSizeOptions: [10, 15, 25, 50],
                paging: true,
                rowStyle: (rowData, index) => ({
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#fbfcfe',
                }),
                search: true,
                searchFieldAlignment: 'right',
                searchFieldStyle: {
                  width: 280,
                  maxWidth: '36vw',
                },
                showFirstLastPageButtons: true,
                showTitle: false,
                sorting: true,
                tableLayout: 'fixed',
                toolbar: true,
              }}
              style={{
                background: '#ffffff',
                boxShadow: 'none',
              }}
            />
          </section>
        </div>
      </main>

      <Dialog
        open={state.dialogOpen}
        onClose={closeDialog}
        aria-labelledby="reset-password-dialog-title"
        PaperProps={{ className: classes.dialogPaper }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          id="reset-password-dialog-title"
          className={classes.dialogTitle}
        >
          Reset password
        </DialogTitle>

        <DialogContent>
          <DialogContentText>
            Reset the password for{' '}
            {state.rowData ? state.rowData.Office_Name : 'this MRI center'}?
            The office will need to use the newly reset credentials the next
            time it signs in.
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={closeDialog}
            variant="outlined"
            disabled={state.resetting}
          >
            Cancel
          </Button>
          <Button
            onClick={resetPassword}
            className={classes.dangerButton}
            variant="contained"
            disabled={state.resetting}
          >
            {state.resetting ? 'Resetting...' : 'Reset password'}
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default MRIList;